import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Patient } from '@models/patient';
import { PatientService } from '@services/patient.service';
import { AuthService } from '@services/auth.service';
import { NotificationService } from '@services/notification.service';
import { LINK_RELATIONSHIPS, relationshipKind } from '@classes/linked-patients';

@Component({
  selector: 'app-linked-patients',
  templateUrl: './linked-patients.component.html',
  styleUrls: ['./linked-patients.component.css'],
})
export class LinkedPatientsComponent implements OnChanges {
  @Input() patient: Patient;

  links: any[] = [];
  history: any[] = [];
  relationships = LINK_RELATIONSHIPS;
  selectedPatient: Patient;
  relationship = 'Mother';
  managing = false;
  otherDescription = '';
  editingId = '';
  busy = false;

  constructor(
    private patientService: PatientService,
    private auth: AuthService,
    private notification: NotificationService,
    private router: Router
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.patient && this.patient?._id) {
      this.links = (this.patient as any).linkedPatients || [];
      this.history = (this.patient as any).history || [];
      this.load();
    }
  }

  load(): void {
    if (!this.patient?._id) {
      return;
    }
    this.patientService.listLinks(String(this.patient._id)).subscribe({
      next: (res) => {
        this.links = res?.links || [];
        this.history = res?.history || [];
      },
    });
  }

  onSelectPatient(patient: Patient): void {
    this.selectedPatient = patient;
  }

  relationshipLabel(link: any): string {
    const value = String(link?.relationship || '');
    if (value === 'Other' && link?.otherDescription) {
      return `Other — ${link.otherDescription}`;
    }
    return value;
  }

  kindIcon(link: any): string {
    const kind = relationshipKind(link?.relationship);
    if (kind === 'FAMILY') {
      return 'groups';
    }
    if (kind === 'HOUSEHOLD') {
      return 'home';
    }
    return 'link';
  }

  openProfile(link: any): void {
    const id = link?.patient?._id || link?.patient;
    if (id) {
      this.router.navigate(['/admin/patient', id]);
    }
  }

  canSave(): boolean {
    if (this.busy || !this.selectedPatient?._id) {
      return false;
    }
    if (String(this.selectedPatient._id) === String(this.patient?._id)) {
      return false;
    }
    if (this.relationship === 'Other' && !String(this.otherDescription || '').trim()) {
      return false;
    }
    return true;
  }

  addOrUpdate(): void {
    if (!this.canSave() && !this.editingId) {
      return;
    }
    if (this.relationship === 'Other' && !String(this.otherDescription || '').trim()) {
      this.notification.show('error', 'Describe the Other relationship');
      return;
    }
    this.busy = true;
    const payload = {
      linkedPatientId: this.selectedPatient?._id,
      relationship: this.relationship,
      otherDescription: this.otherDescription,
      initials: this.auth.currentUser?.initials,
      user: this.auth.currentUser?._id,
    };
    const request = this.editingId
      ? this.patientService.updateLink(String(this.patient._id), this.editingId, payload)
      : this.patientService.addLink(String(this.patient._id), payload);
    const wasEdit = !!this.editingId;
    request.subscribe({
      next: (res) => {
        this.busy = false;
        this.links = res?.links || [];
        this.history = res?.history || [];
        this.selectedPatient = null;
        this.otherDescription = '';
        this.editingId = '';
        this.notification.show('success', wasEdit ? 'Linked patient updated' : 'Linked patient added');
      },
      error: (err) => {
        this.busy = false;
        this.notification.show('error', err?.error?.message || 'Could not save the linked patient');
      },
    });
  }

  edit(link: any): void {
    this.editingId = String(link?._id || '');
    this.relationship = link?.relationship || 'Spouse';
    this.otherDescription = link?.otherDescription || '';
    this.selectedPatient = link?.patient;
  }

  remove(link: any): void {
    this.busy = true;
    this.patientService
      .removeLink(String(this.patient._id), String(link._id), {
        initials: this.auth.currentUser?.initials,
        user: this.auth.currentUser?._id,
      })
      .subscribe({
        next: (res) => {
          this.busy = false;
          this.links = res?.links || [];
          this.history = res?.history || [];
          this.notification.show('success', 'Linked patient removed');
        },
        error: (err) => {
          this.busy = false;
          this.notification.show(
            'error',
            err?.error?.message || 'Could not remove the linked patient'
          );
        },
      });
  }
}
